import React, { ReactNode, useContext } from 'react';
import { useSelector } from 'react-redux';

import { AttentionMessage } from '@eika/attention-message';
import { IconTypes } from '@eika/icon';
import { Link } from '@eika/link';

import { useConfig } from '../config';
import { useKunde } from '../kundeinfo/kunde-context';
import { getExternalUrlForSkadetype } from '../landingsside/deep-link-handler';
import { AppState } from '../reducers';
import { formatShortDate } from '../sporsmalsflyt/format';
import { DateAsString } from '../types/generated-types';
import { dataTrackIdPrefix } from '../utils/piwik-utils';
import { avtaleContext, AvtaleContext } from './avtale-context';

const getInfoTekstForAvtale = (fulltNavn: string | undefined, isPrivatAvtale: boolean) => {
    if (fulltNavn) {
        return fulltNavn;
    }
    if (isPrivatAvtale) return 'din private avtale';
    return 'din bedriftsavtale';
};

export enum AvtaleInfoMessageType {
    FinnerIkkeRiktig,
    IngenForsikring,
}

type AvtaleInfoMessageProps = {
    avtaleInfoMessageType: AvtaleInfoMessageType;
    skadedato: DateAsString | undefined;
    componentToShowIfNoOtherAvtaler: ReactNode | null;
    className: string;
};

const AvtaleInfoMessage = ({
    componentToShowIfNoOtherAvtaler,
    avtaleInfoMessageType,
    skadedato,
    className,
}: AvtaleInfoMessageProps) => {
    const { url } = useConfig().links.avtalebyttekeycloaklink;
    const skademeldingState = useSelector((state: AppState) => state.skademelding);
    const { skadetype: skademeldingstype } = skademeldingState;
    const { kundeHasOtherAvtaler, isEikaForsikring, isPrivatAvtale }: AvtaleContext = useContext(avtaleContext);

    const { kundeinfo } = useKunde();
    const fulltNavn = kundeinfo ? `${kundeinfo?.fornavn} ${kundeinfo?.etternavn}`?.trim() : undefined;

    const startOfText =
        avtaleInfoMessageType === AvtaleInfoMessageType.FinnerIkkeRiktig
            ? `Du ser kun gyldige forsikringer`
            : `Du har ingen gyldige forsikringer${skadedato ? ` per ${formatShortDate(skadedato)}` : ''}`;

    if (kundeHasOtherAvtaler && isEikaForsikring) {
        return (
            <div className={className}>
                <AttentionMessage
                    id="skadedato-and-forsikring-sporsmal__has-other-avtaler-attention-message"
                    type="info"
                    text={`${startOfText} knyttet til ${getInfoTekstForAvtale(fulltNavn, isPrivatAvtale)}. Hvis du skal melde skade på en annen avtale kan du bytte her. Eller så kan du fylle inn forsikringsnummeret ditt under.`}
                    icon={IconTypes.INFO_24}
                    clickableItem={
                        <Link
                            href={`${url}?returnurl=${getExternalUrlForSkadetype(skademeldingstype)}`}
                            data-track-id={`${dataTrackIdPrefix}-avtalebytte`}
                            data-track-linkname="bytt-avtale"
                        >
                            Bytt avtale
                        </Link>
                    }
                />
            </div>
        );
    }

    return componentToShowIfNoOtherAvtaler;
};

export default AvtaleInfoMessage;
